import { scValToNative } from '@stellar/stellar-base';
import {
    Address,
    Contract,
    Operation,
    rpc as SorobanRpc,
    TransactionBuilder,
} from '@stellar/stellar-sdk/no-axios';

/**
 * Read-only Soroban simulation: standard Stellar Asset Contract (SEP-41) `balance(Address) -> i128`.
 * Used for “public” wallet balance (e.g. testnet USDC SAC `CBIEL…`).
 */
export async function fetchSacBalanceRaw({
    rpcUrl,
    networkPassphrase,
    tokenContractId,
    ownerPublicKey,
}) {
    const server = new SorobanRpc.Server(rpcUrl, { allowHttp: String(rpcUrl).startsWith('http://') });
    let account;
    try {
        account = await server.getAccount(ownerPublicKey);
    } catch (e) {
        const msg = String(e?.message || e || '');
        if (/not found|404/i.test(msg) || e?.response?.status === 404) {
            return 0n;
        }
        throw e;
    }

    const c = new Contract(tokenContractId);
    const owner = Address.fromString(ownerPublicKey);
    const op = c.call('balance', owner.toScVal());

    const tx = new TransactionBuilder(account, {
        fee: '100000',
        networkPassphrase,
    })
        .addOperation(op)
        .setTimeout(60)
        .build();

    const sim = await server.simulateTransaction(tx);
    if (SorobanRpc.Api.isSimulationError(sim)) {
        const err = typeof sim.error === 'string' ? sim.error : JSON.stringify(sim.error || {});
        throw new Error(err || 'Soroban simulation failed');
    }
    if (!SorobanRpc.Api.isSimulationSuccess(sim)) {
        throw new Error('Unexpected Soroban simulation response');
    }

    const rv = sim.result?.retval;
    if (!rv || rv.switch().name === 'scvVoid') {
        return 0n;
    }

    const n = scValToNative(rv);
    if (typeof n === 'bigint') return n;
    if (typeof n === 'number' && Number.isFinite(n)) return BigInt(Math.trunc(n));
    if (typeof n === 'string' && /^-?\d+$/.test(n)) return BigInt(n);
    throw new Error(`Unexpected SAC balance type: ${typeof n}`);
}

/** Format i128 token amount (non-negative) for UI, trimming trailing zeros. */
export function formatSacAmountForDisplay(amountBigInt, decimals) {
    const d = Number(decimals);
    if (!Number.isFinite(d) || d < 0 || d > 18) {
        return String(amountBigInt);
    }
    const neg = amountBigInt < 0n;
    const a = neg ? -amountBigInt : amountBigInt;
    const scale = 10n ** BigInt(d);
    const whole = a / scale;
    const frac = a % scale;
    let fracStr = frac.toString().padStart(d, '0');
    fracStr = fracStr.replace(/0+$/, '');
    const out = fracStr ? `${whole}.${fracStr}` : `${whole}`;
    const num = parseFloat(out);
    if (!Number.isFinite(num)) return out;
    return neg ? String(-num) : String(num);
}
